// Request body for POST /assignments/{assignmentId}/submissions/batch.
//
// Replaces the bulkDrafts staging layer: the same page references, sent straight
// at the submissions they belong to instead of at an intermediate draft. Files are
// already in storage by the time this runs — the drag-drop step uploads each one
// as it lands — so a page is a reference, never bytes.
//
// Two shapes, never both on one answer: scanned pages carry `fileUrls`
// [{fileUrl, pageIndex}], a typed response carries `text`.
import { isManual, pageIndexOf, manualTextOf } from './pageRules';

// Mirrors the server-side limits so the teacher gets a readable message here
// rather than a 400 after the round trip.
export const MAX_STUDENTS_PER_BATCH = 200;
export const MAX_PAGES_PER_STUDENT = 100;
export const MAX_PAGES_PER_BATCH = 1000;
export const MAX_MANUAL_TEXT_CHARS = 50000;

// A My Drafts document only ever has the one question.
const SERIAL_NUMBER = '1';

// pageIndex is 0-based and always 0 for images and single-page documents.
const toPageRef = (page) => ({
  fileUrl: page.fileUrl,
  pageIndex: pageIndexOf(page),
});

// A file page whose upload never returned a url has nothing to reference, and an
// empty typed page has nothing to store. Neither can be expressed, so neither is
// sent — null means "not routable".
const toEntry = (page) => {
  if (isManual(page)) {
    const text = manualTextOf(page);
    return text ? { text } : null;
  }
  return page?.fileUrl ? { page: toPageRef(page) } : null;
};

const indexPagesById = (pages) =>
  new Map((pages || []).map((page) => [page.id, page]));

const attemptedEntriesFor = (pageIds, pageById) =>
  (pageIds || []).map((pageId) => {
    const page = pageById.get(pageId);
    return { pageId, page, entry: toEntry(page) };
  });

const isRouted = ({ entry }) => entry !== null;

// An empty typed page has nothing to lose. A file page whose upload never returned
// a url is the teacher's work going missing, which is the drop worth refusing.
const isLost = ({ page, entry }) => entry === null && !isManual(page);

// Nothing is filtered away here: an unroutable page is a page the teacher will
// never see marked, so it has to survive as far as the caller.
const partition = (attempted) => ({
  routed: attempted.filter(isRouted),
  lostPageIds: attempted.filter(isLost).map(({ pageId }) => pageId),
  wasGivenPages: attempted.length > 0,
});

const routeStudents = (studentAssignments, pageById) =>
  Object.entries(studentAssignments || {}).map(([studentId, pageIds]) => ({
    studentId,
    ...partition(attemptedEntriesFor(pageIds, pageById)),
  }));

// Students left with nothing to send are not posted empty — the server rejects the
// whole batch on an empty answer.
const sendableOf = (routes) => routes.filter(({ routed }) => routed.length > 0);

// A student given pages who ends with none vanishes from the batch; a student
// given none was never in it.
const vanishedIn = (routes) =>
  routes
    .filter(({ routed, wasGivenPages }) => !routed.length && wasGivenPages)
    .map(({ studentId }) => studentId);

const lostByStudentIn = (routes) =>
  Object.fromEntries(
    routes
      .filter(({ lostPageIds }) => lostPageIds.length)
      .map(({ studentId, lostPageIds }) => [studentId, lostPageIds]),
  );

const droppedIn = (routes) => ({
  lostPageIds: routes.flatMap(({ lostPageIds }) => lostPageIds),
  lostPageIdsByStudent: lostByStudentIn(routes),
  vanishedStudentIds: vanishedIn(routes),
});

const findStudentById = (students, studentId) =>
  (students || []).find((s) => String(s.id) === String(studentId));

const nameOf = (roster, studentId) => {
  const student = findStudentById(roster, studentId);
  return student?.studentName || student?.name || 'A student';
};

/** Roster names, joined the way a sentence would name them. */
export const listStudentNames = (studentIds, roster) => {
  const names = studentIds.map((id) => nameOf(roster, id));
  if (names.length < 2) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const isLocalStudentId = (studentId) => /^(local-|temp_)/.test(String(studentId));


/**
 * Built honestly from whatever was routed, INCLUDING the combination the server
 * rejects. Collapsing "scans and typed text" to one shape here would silently
 * discard the other half of the teacher's work; leaving both in lets
 * submissionBatchShapeError name the problem so they can fix it.
 *
 * Several typed pages for one student are one response in several parts, so they
 * join rather than compete.
 */
const toAnswer = (entries) => {
  const text = entries
    .map(({ entry }) => entry.text)
    .filter(Boolean)
    .join('\n\n');
  const fileUrls = entries.map(({ entry }) => entry.page).filter(Boolean);
  return {
    serialNumber: SERIAL_NUMBER,
    ...(fileUrls.length ? { fileUrls } : {}),
    ...(text ? { text } : {}),
  };
};

const toStudent = (studentId, entries, students) => {
  const isLocal = isLocalStudentId(studentId);
  // Only the walk-in case needs the roster scan, and it is the rare one.
  const student = isLocal ? findStudentById(students, studentId) : null;
  return {
    studentId,
    answers: [toAnswer(entries)],
    ...(isLocal && student
      ? {
          studentName:
            student.studentName || student.name || 'Unknown Student',
        }
      : {}),
  };
};

const payloadFor = (routes, students) => ({
  students: routes.map(({ studentId, routed }) =>
    toStudent(studentId, routed, students),
  ),
});

const routedPageIdsFor = (routes) =>
  Object.fromEntries(
    routes.map(({ studentId, routed }) => [
      studentId,
      routed.map(({ pageId }) => pageId),
    ]),
  );

/**
 * @returns {{payload: object, routedPageIdsByStudent: object, dropped: object}} —
 * `routedPageIdsByStudent` is the local page id behind every entry actually sent,
 * in send order. Extraction and the Converting poll answer positionally against
 * it, so re-deriving the order later would drift the moment a page is skipped.
 *
 * `dropped` is everything that could NOT be sent, so the commit can refuse rather
 * than shrink the batch behind the teacher's back — see submissionBatchDropError.
 */
export const buildSubmissionBatchPayload = (
  pages,
  studentAssignments,
  students = [],
) => {
  const routes = routeStudents(studentAssignments, indexPagesById(pages));
  const sendable = sendableOf(routes);
  return {
    payload: payloadFor(sendable, students),
    routedPageIdsByStudent: routedPageIdsFor(sendable),
    dropped: droppedIn(routes),
  };
};

export const isEmptySubmissionBatch = ({ students = [] } = {}) =>
  students.length === 0;

const answersOf = (student) => student.answers || [];

const isMixedShape = (answer) =>
  Boolean(answer.text) && Boolean(answer.fileUrls?.length);

/**
 * The server requires exactly one of fileUrls or text per answer
 * (SubmissionBatchValidator.requireExactlyOneShape), so a student holding both a
 * scan and a typed response cannot be expressed. Caught here so the teacher is
 * told which student to fix rather than losing one half of the work silently.
 */
export const submissionBatchShapeError = ({ students = [] } = {}, roster = []) => {
  const mixed = students.find((student) => answersOf(student).some(isMixedShape));
  if (!mixed) return null;
  // A walk-in student carries their name on the payload (their local id was
  // replaced); a roster student is only an id here, so look them up.
  const who = mixed.studentName || nameOf(roster, mixed.studentId);
  return `${who} has both scanned pages and a typed response. Keep one or the other — a response can be uploaded or typed, not both.`;
};

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const vanishedError = (studentIds, roster) =>
  `${listStudentNames(studentIds, roster)} ${studentIds.length > 1 ? 'have' : 'has'} no response that can be submitted — those pages never finished uploading. Add them again, or remove the student, before continuing.`;

const lostPagesError = (lostByStudent, roster) => {
  const owners = Object.keys(lostByStudent);
  const count = Object.values(lostByStudent).flat().length;
  return `${plural(count, 'page')} for ${listStudentNames(owners, roster)} could not be submitted — they never finished uploading. Add them again, or remove them, before continuing.`;
};

/**
 * The teacher-facing reason this batch would lose work, or null if it would not.
 *
 * An unroutable page is left out of the payload and a student whose every page is
 * unroutable out of the batch, and neither used to show anywhere: only a WHOLLY
 * empty batch raised EMPTY_STUDENTS, so a partial drop meant that student never
 * got a submission, never got feedback, and nobody was told. Named here so the
 * commit can refuse instead of quietly shrinking.
 */
export const submissionBatchDropError = (
  { lostPageIdsByStudent = {}, vanishedStudentIds = [] } = {},
  roster = [],
) => {
  if (vanishedStudentIds.length) return vanishedError(vanishedStudentIds, roster);
  if (Object.keys(lostPageIdsByStudent).length)
    return lostPagesError(lostPageIdsByStudent, roster);
  return null;
};

const pagesOf = (student) =>
  answersOf(student).flatMap((answer) => answer.fileUrls || []);

const totalPages = (students) =>
  students.reduce((sum, student) => sum + pagesOf(student).length, 0);

const hasOverlongText = (students) =>
  students.some((student) =>
    answersOf(student).some(
      (answer) => (answer.text || '').length > MAX_MANUAL_TEXT_CHARS,
    ),
  );

/**
 * The teacher-facing reason this batch breaches a limit, or null if it does not.
 * Deliberately more specific than the server's generic LIMIT_EXCEEDED: it can name
 * the actual count, which is what tells the teacher how much to remove.
 */
export const submissionBatchLimitError = ({ students = [] } = {}) => {
  const pages = totalPages(students);
  if (students.length > MAX_STUDENTS_PER_BATCH)
    return `A bulk upload can cover at most ${MAX_STUDENTS_PER_BATCH} students; this one has ${students.length}.`;
  if (students.some((student) => pagesOf(student).length > MAX_PAGES_PER_STUDENT))
    return `A student can hold at most ${MAX_PAGES_PER_STUDENT} pages in one upload.`;
  if (pages > MAX_PAGES_PER_BATCH)
    return `A bulk upload can hold at most ${MAX_PAGES_PER_BATCH} pages; this one has ${pages}.`;
  if (hasOverlongText(students))
    return `A typed response is too long — keep each one under ${MAX_MANUAL_TEXT_CHARS} characters.`;
  return null;
};
